import React from "react"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { GENERAL_CONFIG } from "../../config/general.config"

export const CandidHealthSetupGuideComponent: React.FunctionComponent = () => {
    const { supportEmail, sftp } = GENERAL_CONFIG

    return (
        <div
            data-produktly="integrations-candid-health-setup"
            className="max-w-[720px] flex flex-col gap-[24px]"
        >
            <PrimaryText weight="medium">
                To share your ERA data from Candid we need to establish a Data
                Sharing protocol. Here's how to get set up:
            </PrimaryText>

            <div>
                <PrimaryText weight="semibold">
                    Step 1: Request SFTP Data Sharing
                </PrimaryText>

                <PrimaryText className="mt-[8px]" weight="medium">
                    Submit a request to the Candid Support Team to share ERA 835
                    data with Nirvana and cc your CS representative or{" "}
                    <a
                        className="!text-brand-vibrantPurple"
                        href={`mailto:${supportEmail}`}
                    >
                        {supportEmail}
                    </a>
                    . Use the email template below:
                </PrimaryText>

                <div
                    className="
                        mt-[12px] rounded-[8px] p-[16px]
                        bg-brand-offWhite border border-solid border-brand-warmLight2
                        select-all
                    "
                >
                    <PrimaryText weight="semibold">
                        Subject: ERA 835 Data Sharing Request – Nirvana
                        Integration
                    </PrimaryText>

                    <PrimaryText className="mt-[16px]" weight="medium">
                        Hi Candid Support Team,
                    </PrimaryText>

                    <PrimaryText className="mt-[16px]" weight="medium">
                        I'd like to request to share ERA 835 data with Nirvana
                        for our practice via SFTP.
                    </PrimaryText>

                    <PrimaryText className="mt-[16px]" weight="medium">
                        Once approved, we will notify Nirvana to provision an
                        SFTP username for our practice. Nirvana will share the
                        username, hostname, and port with Candid, and request
                        that Candid generate an SSH key pair and share the
                        public key with Nirvana.
                    </PrimaryText>

                    <PrimaryText className="mt-[16px]" weight="medium">
                        Thank you,
                    </PrimaryText>

                    <PrimaryText weight="medium">[Your Name]</PrimaryText>
                </div>

                <PrimaryText
                    className="mt-[8px]"
                    size={13}
                    lineHeight={18}
                    color={NIRVANA_COLORS.brand.grey7}
                >
                    Select all and copy to use as your email template.
                </PrimaryText>
            </div>

            <div>
                <PrimaryText weight="semibold">
                    Step 2: Notify Your Nirvana Representative
                </PrimaryText>

                <PrimaryText className="mt-[8px]" weight="medium">
                    Once Candid confirms your request, reach out to your Nirvana
                    representative or{" "}
                    <a
                        className="!text-brand-vibrantPurple"
                        href={`mailto:${supportEmail}`}
                    >
                        {supportEmail}
                    </a>{" "}
                    to let them know the request has been approved. Nirvana will
                    then provide Candid directly with the following SFTP server
                    details and handle the rest:
                </PrimaryText>

                <ul className="mt-[12px] mb-0 pl-[20px] list-disc">
                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Hostname: SFTP server address (e.g.,{" "}
                            {sftp.hostnameExample})
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Port: {sftp.port}
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Username: Your authorized SFTP username
                        </PrimaryText>
                    </li>
                </ul>
            </div>

            <div>
                <PrimaryText weight="medium">
                    Once you've completed the steps above, Candid and Nirvana
                    take care of the technical setup on your behalf:
                </PrimaryText>

                <ol className="mt-[12px] mb-0 pl-[20px] list-decimal">
                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Nirvana provides Candid with the necessary SFTP
                            server details.
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Candid generates an SSH key pair and shares the
                            public key with Nirvana.
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Nirvana configures your SFTP account.
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Candid finalizes the configuration and enables ERA
                            data transfer.
                        </PrimaryText>
                    </li>

                    <li className="mb-[4px]">
                        <PrimaryText className="inline" weight="medium">
                            Nirvana verifies successful receipt of ERA data.
                        </PrimaryText>
                    </li>
                </ol>
            </div>
        </div>
    )
}
